// import { useMediaQuery } from "@material-ui/core";
import { navigateToSchool } from "@shared-utils/route.util";
import { useRouter } from "next/router";
import React, { useCallback } from "react";
// import useTranslator from "@utils/useTranslator";
// import { sendCourseAmplitudeData } from "shared/services/amplitude.service";
// import AppsumoInputs from "src/organisms/AppsumoInputs";
// import LeadGenerationInputs from "src/organisms/LeadGenerationInputs";
// import Image from 'next/image';
// import Button from "@/components/elements/Button";
// import GuestDetailsModal from "@/components/modules/GuestDetailsModal";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import Heading from "@/components/atoms/Heading";
import HeroPill from "@/components/atoms/HeroPill";
import CtaButtonGroup, {
  CtaActionType, ScheduleADemoSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import LeadGenerationInputs from "@/components/organisms/LeadGenerationInputs";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { UCE_CALENDLY_Affliate, UCE_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import useGlobalStore from "@/shared/store/GraphyHome/useGlobalStore";
import { useGraphyHomeStore } from "@/shared/store/GraphyHome/useGraphyHomeStore";
import { useDashboardListModalStore } from "@/shared/store/useDashboardListModalStore";
import Image from "next/image";
import HeaderTestimonial from "./HeaderTestimonial";

type HeaderDataFormat = {
  ctaRedirectLink: string | undefined;
  ctaRedirect: string;
  type: string;
  id: string;
  subHeading: string;
  title: string;
  titleType: string;
  subTitle: string;
  defaultCountry?: string;
  emailOnly?: boolean;
  validateMobile?: boolean;
  cta1Text?: string;
  cta2Text?: string;
  gradientColor: string;
  amplitudeSrc: string;
  headerType?: string; //can be 'lottie', 'image' or 'compare'
  heroImage?: string; //when type = 'image'
  ctaType: string; // can be link, signup, email, lead-generation, appsumo
  ctaText: string;
  ctaLink: string;
  ctaPromoText: string;
  centerPromoText: boolean;
  competitorLogo?: string; //when type = 'compare'
  footer?: boolean;
  titleGradient?: string;
  crownImage?: string;
  testimonialData: {
    name: string;
    position: string;
    avatar: string;
    review: string;
  };
  showBookBtn?: boolean;
};

interface HeaderProps {
  data: HeaderDataFormat;
  className?: string;
  langFile?: string;
  handleLaunch: (amplitudeSrc: string) => void;
}

const HeroHeader: React.FC<HeaderProps> = ({
  data,
  handleLaunch,
  langFile,
  className,
}: HeaderProps) => {
  const authenticated = useGlobalStore((state) => state.authenticated);
  const isFooter = data?.footer ?? false;
  const headerId = data?.id ? `.${data?.id}` : "";
  const router = useRouter();
  
  const url = router.query.page;
  const isLMS = url === "lms-software";
  const isOnlineCoursePlatform = url === "online-course-platform";

  const handleButtonClick = (isOnlineCoursePlatform: boolean) => {
    if (isOnlineCoursePlatform) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const { openDashboardListModal } = useDashboardListModalStore(
    ({ openDashboardListModal }) => ({
      openDashboardListModal,
    })
  );


  
  const showDefaultCTA =
    data?.ctaType !== "email" &&
    data?.ctaType !== "lead-generation" &&
    data?.ctaType !== "appsumo" &&
    data?.ctaType !== "link";

  const isInstructor = useGlobalStore((state) => state.isInstructor);

  const graphySchools = useGraphyHomeStore((state) => state.graphySchools);

  const navigateDashboard = () => {
    if (graphySchools && graphySchools.length > 1) {
      openDashboardListModal();
    } else {
      if (graphySchools && isInstructor) {
        navigateToSchool(graphySchools?.[0]?.subdomain, "home");
      } else {
        openDashboardListModal();
      }
    }
  };

  const renderCTAFooterText = useCallback(() => {
    const text = data?.ctaPromoText;

    if (text) {
      return (
        <div
          className="mt-4 text-body-16 text-grey-3 md:whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: `<p>${text}</p>` }}
        />
      );
    } else
      return (
        <p className="text-body-16 text-grey-3 md:w-96 md:whitespace-pre-wrap">
          Over <span className="font-semibold">150K+ creators</span> have
          launched their online teaching businesses using Graphy.
        </p>
      );
  }, [data]);

  const renderHeadings = useCallback(() => {
    return (
      <>
        {data?.crownImage && (
          <img
            src={data.crownImage}
            alt="header crown image"
            className="mb-5"
          />
        )}
        {data?.headerType === "text" && (
          <HeroPill category={data?.subHeading} className="mb-5" />
        )}
        <div>
          <h1
            className={`${
              data?.titleType && data?.titleType == "h2"
                ? "text-2xl lg:text-4xl"
                : `text-3xl  ${
                    data.headerType === "testimonial"
                      ? "lg:text-title-32"
                      : "lg:text-title-48"
                  }  `
            } font-obviously mb-4 text-left text-title-32 text-dark ${
              data?.headerType === "text"
                ? "pb-3 text-center "
                : "md:text-center lg:text-left"
            }`}
          >
            {data?.title}
          </h1>
          {data.subTitle && data?.ctaType !== 'lead-generation-us' && (
            <h3
              className={`  ${
                data?.headerType === "text"
                  ? "pb-1 text-center"
                  : "md:text-center lg:text-left"
              } mt-4 w-full ${
                data?.ctaType === "lead-generation"
                  ? "mb-6 text-title-24"
                  : "text-body-16"
              } text-grey-4`}
            >
              {data.subTitle}
            </h3>
          )}
        </div>
      </>
    );
  }, [data]);

  const renderCTAType = useCallback(() => {
    return (
      <>
        <div
          className={`${data?.headerType === "text" && "mt-12"} flex ${
            data?.ctaType == "signup-only" ? "" : "w-full"
          } flex-col gap-4`}
        >
          {data?.ctaType == "email" && (
            <div className="flex flex-col gap-4">
              {!authenticated && (
                <>
                  {data?.headerType === "testimonial" && renderCTAFooterText()}
                  <div className="flex flex-col whitespace-nowrap md:flex-row">
                    {data?.showBookBtn ? (
                      <CtaButtonGroup
                        ctas={[
                          {
                            type: ButtonTypeEnum.Primary,
                            size: ButtonSizeEnum.Regular,
                            text: data?.cta1Text || "Start for free",
                            actionType:
                              data?.ctaRedirect === "link"
                                ? CtaActionType.RedirectHref
                                : CtaActionType.SignUp,
                            href: data?.ctaRedirectLink,
                            amplitudeEvent: {
                              eventName:
                                LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                              eventProperties: {
                                source: "hero",
                              },
                            },
                          },
                          {
                            type: ButtonTypeEnum.Secondary,
                            size: ButtonSizeEnum.Regular,
                            text: data?.cta2Text || "Book a Demo",
                            actionType: CtaActionType.BookDemo,
                            amplitudeEvent: {
                              eventName:
                                LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                              eventProperties: {
                                source: "hero",
                              },
                            }, //schedule type
                          },
                        ]}
                      />
                    ) : (
                      <CtaButtonGroup
                        ctas={[
                          {
                            type: ButtonTypeEnum.Primary,
                            size: ButtonSizeEnum.Regular,
                            text: data?.cta1Text || "Start for free",
                            actionType:
                              data?.ctaRedirect === "link"
                                ? CtaActionType.RedirectHref
                                : CtaActionType.SignUp,
                            href: data?.ctaRedirectLink,
                            amplitudeEvent: {
                              eventName:
                                LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                              eventProperties: {
                                source: "hero",
                              },
                            },
                          },
                        ]}
                      />
                    )}
                  </div>
                  {data?.headerType !== "testimonial" && renderCTAFooterText()}
                </>
              )}
            </div>
          )}
          {data?.ctaType == "lead-generation" && (
            <div
              className={` ${
                data?.headerType === "testimonial" &&
                "flex flex-col items-center justify-center lg:min-w-[450px] "
              }${
                data?.headerType === "text" &&
                "flex flex-col items-center justify-center"
              }`}
            >
              <LeadGenerationInputs
                isFooter
                // placeholderText={emailPlaceholderText}
                emailOnly={false}
                validateMobile
                footerEvent={isFooter}
                header
                ctaText={data?.ctaText}
                defaultCountry={data?.defaultCountry}
                sourceSent="body_end"
              />
              {data?.ctaType == "lead-generation" && isFooter && (
                <h5
                  className={`mt-6  font-medium leading-6 text-slate03 ${
                    data?.centerPromoText && "text-center"
                  }`}
                >
                  {renderCTAFooterText()}
                </h5>
              )}
            </div>
          )}

          {data?.ctaType == "lead-generation-us" && (
            <div
              className={` ${
                data?.headerType === "testimonial" &&
                "flex flex-col items-center justify-center lg:min-w-[450px] "
              }${
                data?.headerType === "text" &&
                "flex flex-col items-center justify-center"
              }`}
            >
              <div className="flex flex-col">
                <p className="mb-6 mt-4 w-full text-title-32 text-black md:text-center lg:text-left">
                  {
                      isLMS ? "Get started with Graphy today" : "Start monetizing your knowledge today"   
                  }
                </p>
                <p>
                  Join our 150,000+ creators who use Graphy to create courses,
                  sell digital products, run webinars, and more
                </p>
                <div className="mt-8 grid self-stretch whitespace-nowrap">
                  <CtaButtonGroup
                    align="md:center"
                    ctas={[
                      {
                        type: ButtonTypeEnum.Primary,
                        size: ButtonSizeEnum.Regular,
                        text: "Schedule a demo",
                        href: isOnlineCoursePlatform ? undefined : `${UCE_CALENDLY_LINK}?page_source=page-end`,
                        onClick: isOnlineCoursePlatform ? () => handleButtonClick(isOnlineCoursePlatform) : undefined,
                        actionType: isOnlineCoursePlatform ? CtaActionType.NoAction : CtaActionType.DelayedHrefBlank,
                        amplitudeEvent: {
                          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                          eventProperties: {
                            source: ScheduleADemoSourceEnum.body_end,
                          },
                        },
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {data?.ctaType == "signup-only" && (
            <CtaButtonGroup
              wideButton
              ctas={[
                {
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Regular,
                  text: data?.cta1Text || "Start for free",
                  actionType:
                    data?.ctaRedirect === "link"
                      ? CtaActionType.RedirectHref
                      : CtaActionType.SignUp,
                  href: data?.ctaRedirectLink,
                  amplitudeEvent: {
                    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                    eventProperties: {
                      source: "hero",
                    },
                  },
                },
              ]}
            />
          )}
          {data?.ctaType == "appsumo" && (
            <div
              className={`w-full ${
                data?.headerType === "text" &&
                "flex flex-col items-center justify-center"
              }`}
            >
              <div className={`mx-auto w-full md:ml-2`}>
                {/* <AppsumoInputs /> */}
              </div>
            </div>
          )}
          {data?.ctaType == "link" && (
            <div className="mt-6 flex flex-col items-center justify-center md:flex-row lg:space-x-5 lg:space-y-0">
              <a href={data?.ctaLink} className="relative">
                <CtaButtonGroup
                  ctas={[
                    {
                      type: ButtonTypeEnum.Primary,
                      size: ButtonSizeEnum.Regular,
                      text: data?.cta1Text || "Start for free",
                      actionType:
                        data?.ctaRedirect === "link"
                          ? CtaActionType.RedirectHref
                          : CtaActionType.SignUp,
                      href: data?.ctaRedirectLink,
                      amplitudeEvent: {
                        eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                        eventProperties: {
                          source: "hero",
                        },
                      },
                    },
                  ]}
                />
              </a>
            </div>
          )}
          {data?.ctaType === "schedule-demo" && (
            <CtaButtonGroup
              ctas={[
                {
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Regular,
                  text: data?.cta1Text || "Start for free",
                  actionType:
                    data?.ctaRedirect === "link"
                      ? CtaActionType.RedirectHref
                      : CtaActionType.SignUp,
                  href: data?.ctaRedirectLink,
                  amplitudeEvent: {
                    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
                    eventProperties: {
                      source: "hero",
                    },
                  },
                },
                {
                  type: ButtonTypeEnum.Secondary,
                  size: ButtonSizeEnum.Regular,
                  text: data?.cta2Text || "",
                  actionType: CtaActionType.BookDemo,
                  amplitudeEvent: {
                    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                    eventProperties: {
                      source: "hero",
                    },
                  }, //schedule type
                },
              ]}
            />
          )}

          {data?.ctaType == "affiliate-us" && (
            <div
              className={` ${
                data?.headerType === "testimonial" &&
                "flex flex-col items-center justify-center lg:min-w-[450px] "
              }${
                data?.headerType === "text" &&
                "flex flex-col items-center justify-center"
              }`}
            >
              <div className="flex flex-col">
                <Heading
                  heading={[
                    { text: "Ready to earn exciting\n", highlight: false },
                    { text: "monthly payouts?", highlight: true },
                  ]}
                  fontStyle="text-title-20 sm:text-title-32"
                  as="h6"
                />
                <p className="mt-6 text-body-16">
                Join our affiliate program today. It’s free and takes just seconds to set up!
                </p>
                <div className="mt-8 grid self-stretch whitespace-nowrap">
                  <CtaButtonGroup
                    align="md:center"
                    ctas={[
                      {
                        type: ButtonTypeEnum.Primary,
                        size: ButtonSizeEnum.Regular,
                        text: "Talk to an Affiliate Expert",
                        href: `${UCE_CALENDLY_Affliate}`,
                        actionType: CtaActionType.RedirectHref,
                        amplitudeEvent: {
                          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
                          eventProperties: {
                            source: ScheduleADemoSourceEnum.body_end,
                          },
                        },
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {data.ctaType && showDefaultCTA && (
            <div>
              <div className="mt-6 flex flex-col items-center justify-center lg:flex-row lg:space-x-5 lg:space-y-0">
                <div className="relative">
                  {/* <CtaButtonGroup
                    ctas={[
                      {
                        type: ButtonTypeEnum.Primary,
                        size: ButtonSizeEnum.Regular,
                        text: data?.cta1Text || "",
                        actionType: CtaActionType.SignUp,
                      },
                    ]}
                  /> */}
                  {/* <Button
                    variant={BUTTON_VARIANT.SOLID}
                    size={SIZE_VARIANTS.MD}
                    onClick={() =>
                      authenticated
                        ? navigateDashboard()
                        : handleLaunch(data.amplitudeSrc)
                    }
                    className="bg-dark justify-center border border-slate02 bg-opacity-100 font-semibold"
                  >
                    {authenticated
                      ? CommonTranslate("cta.go_to_dashboard") ??
                        "Go to dashboard"
                      : Translate(`hero_header${headerId}.cta1Text`) ??
                        data.cta1Text}
                  </Button> */}
                </div>
                {data?.ctaType !== "signup" && (
                  <div className="w-auto">
                    {/* <Button
                      variant={BUTTON_VARIANT.GHOST}
                      size={SIZE_VARIANTS.CUSTOM}
                      onClick={() => scheduleDemoLink(null, null, "hero")}
                      className="text-dark mt-3 justify-center border border-slate02 bg-opacity-50 px-7 py-3 font-semibold lg:mt-0"
                    >
                      {Translate(`hero_header${headerId}.cta2Text`) ??
                        data.cta2Text}
                    </Button> */}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }, [
    authenticated,
    data?.centerPromoText,
    data?.cta1Text,
    data?.cta2Text,
    data?.ctaLink,
    data?.ctaText,
    data.ctaType,
    data?.defaultCountry,
    data?.headerType,
    data?.showBookBtn,
    isFooter,
    renderCTAFooterText,
    showDefaultCTA,
  ]);

  const renderHeaderType = useCallback(() => {
    return (
      <>
        {data?.headerType == "image" && (
          <div className="flex justify-center">
            <Image
              className={"hidden sm:block"}
              src={"/images/Spotillustration.svg"}
              alt={"heroImage"}
              width={400}
              height={400}
            />
            <Image
              className={"sm:hidden"}
              src={"/images/SpotillustrationMobile.svg"}
              alt={"heroImageMobile"}
              width={500}
              height={500}
            />
          </div>
        )}

        {data?.headerType == "affiliate-image" && (
          <div className="flex justify-center">
            <Image
              className={"hidden sm:block"}
              src={"/images/affiliate-illustration.svg"}
              alt={"heroImage"}
              width={400}
              height={400}
            />
            <Image
              className={"sm:hidden"}
              src={"/images/affiliate-illustration.svg"}
              alt={"heroImageMobile"}
              width={500}
              height={500}
            />
          </div>
        )}

        {data?.headerType === "testimonial" && (
          <HeaderTestimonial data={data?.testimonialData} />
        )}
      </>
    );
  }, [data]);

  return (
    <div
      className={`relative  ${className} ${
        data?.headerType === "text" ? "container" : ""
      }`}
    >
      <div
        className={`${
          data?.headerType === "testimonial" || data?.headerType === "image" || data?.headerType === "affiliate-image"
            ? "bg-aurora2"
            : ""
        }`}
      >
      <div
        className={`flex flex-col ${
          data?.headerType === "testimonial" || data?.headerType === "image" || data?.headerType === "affiliate-image"
            ? "justify-between px-4 pb-6 pt-4 md:p-6 lg:p-8"
              : "flex-col-reverse"
          } page-max-width relative mx-auto items-center justify-center sm:mt-10 lg:flex-row lg:whitespace-pre-wrap lg:py-12 pb-16 sm:pb-16 lg:pb-24 px-4 sm:px-6 md:px-8 lg:px-24`}
      >
      <div
        className={`flex flex-col pb-4 ${
          data?.headerType === "text"
            ? "items-center"
            : "max-w-[500px] md:items-center lg:items-start"
             }`}
      >
            {renderHeadings()}
            {renderCTAType()}
          </div>
          {renderHeaderType()}
        </div>
      </div>
    </div>
  );
};

HeroHeader.defaultProps = {
  className: "",
};

export default HeroHeader;
